import { Link } from "react-router-dom";
import { ArrowRight, Target, BrainCircuit, LineChart, ShieldAlert } from "lucide-react";
import Seo from "../components/Seo";
import WeeklyPicks from "../components/landing/WeeklyPicks";
import LandingTerminalPreview from "../components/landing/LandingTerminalPreview";
import WhatWeDoSection from "../components/landing/WhatWeDoSection";

export default function Landing() {
  return (
    <div className="min-h-screen bg-background text-foreground grain relative">
      <Seo
        title="Martingale Playbook · The Ultimate Betting Research Terminal"
        path="/"
        description="The ultimate sports betting research terminal. AI-graded daily picks across MLB, NBA, NHL, NFL and UFC with live DraftKings odds, 1,000-game Monte Carlo simulations, public-trap detection, CLV grading, and an arbitrage scanner. Plus 5 staking strategies: Classic Martingale, Grand, Mini, Anti, and D'Alembert. 3 days free, then $9.99/week."
        keywords="betting research, sports betting terminal, AI handicapper, betting analytics, CLV, closing line value, Monte Carlo betting, Kelly criterion, martingale, anti-martingale, D'Alembert, MLB, NBA, NHL, UFC"
      />
      {/* Top nav */}
      <header className="relative z-10 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="landing-brand">
            <div className="w-8 h-8 border border-[#00D4FF] rotate-45 flex items-center justify-center">
              <span className="-rotate-45 text-[#00D4FF] font-mono text-xs font-bold">M</span>
            </div>
            <div>
              <div className="font-display text-lg leading-none">Martingale</div>
              <div className="overline leading-none mt-1">PLAYBOOK·TERMINAL</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <Link
              to="/login"
              data-testid="landing-login"
              className="px-4 py-2 text-xs font-mono uppercase tracking-widest border border-zinc-800 hover:border-[#00D4FF] hover:text-[#00D4FF]"
            >
              Login
            </Link>
            <Link
              to="/pricing"
              data-testid="landing-pricing"
              className="px-4 py-2 text-xs font-mono uppercase tracking-widest border border-zinc-800 hover:border-[#00D4FF] hover:text-[#00D4FF]"
            >
              Pricing
            </Link>
            <Link
              to="/register"
              data-testid="landing-register"
              className="px-4 py-2 text-xs font-mono uppercase tracking-widest bg-[#00D4FF] text-black hover:brightness-110"
            >
              Start Free Trial
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-24">
        <div className="overline mb-8 flex items-center gap-2">
          <span className="text-[#00D4FF]">●</span>
          THE ULTIMATE BETTING RESEARCH SITE · VOL. 01 / ISSUE 2026
        </div>
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter">
              Sports betting{" "}
              <span className="text-[#00D4FF]">research,</span> built for the
              serious bettor.
            </h1>
            <p className="mt-8 text-zinc-400 text-lg max-w-2xl leading-relaxed">
              Live AI evaluation of every game on the MLB, NBA, NHL, NFL and UFC slate —
              1,000-game Monte Carlo, public-trap detection, market disagreement
              radar, and CLV-graded picks. Then run any of five staking strategies
              against your bankroll. Or none. Your call.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/register"
                data-testid="cta-register"
                className="inline-flex items-center gap-2 px-6 py-4 bg-[#00D4FF] text-black font-mono uppercase tracking-widest text-xs hover:brightness-110"
              >
                Start 3 free days <ArrowRight size={16} strokeWidth={1.5} />
              </Link>
              <Link
                to="/ledger"
                data-testid="cta-ledger"
                className="inline-flex items-center gap-2 px-6 py-4 border border-zinc-700 font-mono uppercase tracking-widest text-xs hover:border-[#00D4FF] hover:text-[#00D4FF]"
              >
                See the public ledger →
              </Link>
              <Link
                to="/pricing"
                data-testid="cta-pricing"
                className="inline-flex items-center gap-2 px-6 py-4 border border-zinc-700 font-mono uppercase tracking-widest text-xs hover:border-[#00D4FF] hover:text-[#00D4FF]"
              >
                $9.99/wk
              </Link>
            </div>
          </div>

          <div className="lg:col-span-4 border-l border-zinc-800 pl-8">
            <div className="overline mb-3">RESEARCH·STACK</div>
            <div className="space-y-3">
              {[
                ["AI", "Claude Sonnet 4.5 reads every matchup"],
                ["SIM", "1,000-game Monte Carlo per pick"],
                ["MKT", "DK live odds + disagreement radar"],
                ["CLV", "Closing-line value graded after each close"],
              ].map(([k, line]) => (
                <div key={k} className="flex items-start gap-3 border-t border-zinc-800 pt-3">
                  <span className="amber-text font-mono text-xs w-12">{k}</span>
                  <span className="text-zinc-400 text-sm">{line}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Live capability teaser — no login, real data */}
      <LandingTerminalPreview />

      {/* Live weekly MLB picks — the conversion hook */}
      <WeeklyPicks />


      {/* Value pillars — research-first */}
      <section className="relative z-10 border-y border-zinc-800 py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-4 gap-0 divide-y lg:divide-y-0 lg:divide-x divide-zinc-800">
          {[
            {
              icon: BrainCircuit,
              title: "AI research engine",
              body: "Claude Sonnet 4.5 reads every matchup — pitcher, weather, injury report, line move — and returns a structured fair line with a confidence band.",
            },
            {
              icon: Target,
              title: "Best Edge ranking",
              body: "Every play on the slate ranked by model edge vs the live DraftKings price. Sim-range bars show how variance-tolerant each pick is.",
            },
            {
              icon: ShieldAlert,
              title: "Trap + No-bet flags",
              body: "Public-trap detector and No-bet analyzer surface the games to fade and the games not worth touching. Discipline first, profit second.",
            },
            {
              icon: LineChart,
              title: "Strategy lab",
              body: "Run any pick through Classic, Grand, Mini, Anti-Martingale, or D'Alembert. See your bust risk before the first wager.",
            },
          ].map(({ icon: Icon, title, body }) => (
            <div key={title} className="px-6 lg:px-8 py-8 first:pl-0 last:pr-0">
              <Icon className="text-[#00D4FF] mb-5" size={22} strokeWidth={1.5} />
              <div className="font-display text-2xl mb-3">{title}</div>
              <p className="text-sm text-zinc-400 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Strategy lab — pick your progression */}
      <section className="relative z-10 py-24 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="overline mb-4">STRATEGY·LAB</div>
            <h2 className="font-display text-4xl lg:text-5xl leading-tight">
              Five progressions.{" "}
              <span className="text-[#00D4FF]">Pick yours.</span>
            </h2>
            <p className="text-zinc-400 mt-5 leading-relaxed text-sm">
              Every pick our AI surfaces can run through any of these staking
              strategies. The calculator simulates each one against your
              bankroll and table limit to show your true bust-risk before you
              touch the first dollar. Or skip the progression entirely — flat
              stake works fine.
            </p>
            <Link
              to="/register"
              data-testid="strategy-cta"
              className="mt-6 inline-flex items-center gap-2 px-5 py-3 bg-[#00D4FF] text-black mono uppercase tracking-widest text-xs hover:brightness-110"
            >
              Open the calculator <ArrowRight size={14} strokeWidth={1.75} />
            </Link>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4" data-testid="strategy-grid">
            {[
              {
                name: "Classic Martingale",
                rule: "Double after each loss. Reset on win.",
                profile: "Aggressive · 4-step bust = 6.25% on a coin-flip",
                tag: "POPULAR",
              },
              {
                name: "Grand Martingale",
                rule: "Double + 1 base unit after each loss.",
                profile: "Larger profit per sequence, larger drawdown",
              },
              {
                name: "Mini Martingale",
                rule: "Classic, capped at N steps. Reset on cap.",
                profile: "Loss-bounded — can't run away",
                tag: "SAFER",
              },
              {
                name: "Anti-Martingale (Paroli)",
                rule: "Double after each WIN. Reset on loss.",
                profile: "Press hot streaks, lose only the base unit",
              },
              {
                name: "D'Alembert",
                rule: "+1 unit on loss, −1 unit on win.",
                profile: "Slowest growth, smallest drawdown",
                tag: "CONSERVATIVE",
              },
              {
                name: "Flat staking",
                rule: "Same unit every wager. No progression.",
                profile: "Pure +EV play. Variance is your friend over time.",
              },
            ].map((s) => (
              <div
                key={s.name}
                className="border border-zinc-800 p-5 hover:border-[#00D4FF]/50 transition-colors"
                data-testid={`strategy-card-${s.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="font-display text-xl leading-tight">{s.name}</div>
                  {s.tag && (
                    <span className="mono text-[9px] tracking-widest px-1.5 py-0.5 border border-[#00D4FF] text-[#00D4FF] shrink-0">
                      {s.tag}
                    </span>
                  )}
                </div>
                <div className="mono text-[11px] text-zinc-300 mb-2">{s.rule}</div>
                <div className="mono text-[10px] text-zinc-500">{s.profile}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What you get — 8-feature walkthrough + buy-in CTA */}
      <WhatWeDoSection />

      {/* Hard truths — keep the disciplined-Martingale credibility, but smaller */}
      <section className="relative z-10 border-t border-zinc-800 py-20 bg-zinc-950/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="overline mb-4">HARD·TRUTHS</div>
            <h2 className="font-display text-3xl lg:text-4xl leading-tight">
              Discipline beats <span className="text-[#00D4FF]">conviction.</span>
            </h2>
          </div>
          <div className="lg:col-span-8 space-y-4 text-zinc-400 leading-relaxed text-sm">
            <p>
              Most "betting systems" sites pretend variance doesn't exist. We
              show you the math and let you decide. A 7-loss streak on a
              coin-flip happens ~0.78% of the time — not never. At -110, you
              need to win 52.38% to break even. Books love progression players
              who size beyond their bankroll.
            </p>
            <p>
              Our calculator and trackers exist precisely to make every one of
              those numbers visible <em>before</em> you place a wager. Use a
              progression if you want one. Don't, if you don't.{" "}
              <Link to="/martingale-method" className="text-[#00D4FF] hover:brightness-125 underline-offset-4 hover:underline" data-testid="hard-truths-method-link">
                Read the full Martingale method →
              </Link>
            </p>
          </div>
        </div>
      </section>

      <footer className="relative z-10 border-t border-zinc-800 py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-3 gap-6 text-xs mono text-zinc-500">
          <div>© 2026 MARTINGALE PLAYBOOK · FOR ENTERTAINMENT & EDUCATION</div>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/about" className="hover:text-[#00D4FF]">About</Link>
            <Link to="/martingale-method" data-testid="footer-martingale-link" className="hover:text-[#00D4FF]">Method</Link>
            <Link to="/arbitrage-betting" data-testid="footer-arbitrage-link" className="hover:text-[#00D4FF]">Arbitrage</Link>
            <Link to="/leaderboard" className="hover:text-[#00D4FF]">Leaderboard</Link>
            <Link to="/pricing" className="hover:text-[#00D4FF]">Pricing</Link>
            <Link to="/terms" className="hover:text-[#00D4FF]">Terms</Link>
            <Link to="/privacy" className="hover:text-[#00D4FF]">Privacy</Link>
            <Link to="/responsible-gambling" className="hover:text-[#00D4FF]">Responsible</Link>
            <Link to="/contact" className="hover:text-[#00D4FF]">Contact</Link>
          </div>
          <div className="amber-text md:text-right">21+ · GAMBLE RESPONSIBLY · 1-800-GAMBLER</div>
        </div>
      </footer>
    </div>
  );
}
